/**
 * Shared transaction hooks for the tx-flow system
 *
 * These hooks provide core transaction functionality used across
 * all action components (Sign, Execute, Batch, etc.)
 *
 * @module tx/shared/hooks
 */
import type { TransactionDetails } from '@safe-global/store/gateway/AUTO_GENERATED/transactions'
import { assertTx, assertOnboard, assertChainInfo, assertProvider } from '@/utils/helpers'
import { useContext, useMemo } from 'react'
import { type TransactionOptions, type SafeTransaction } from '@safe-global/types-kit'
import { sameAddress } from '@safe-global/utils/utils/addresses'
import useSafeInfo from '@/hooks/useSafeInfo'
import { useSafeScope } from '@/components/tx-flow/safe-scope/context'
import useWallet, { useSigner } from '@/hooks/wallets/useWallet'
import useOnboard from '@/hooks/wallets/useOnboard'
import { isSmartContractWallet } from '@/utils/wallets'
import {
  dispatchProposerTxSigning,
  dispatchOnChainSigning,
  dispatchTxConfirmation,
  dispatchTxExecution,
  dispatchTxProposal,
  dispatchTxRelay,
  dispatchTxSigning,
} from '@/services/tx/tx-sender'
import { useHasPendingTxs } from '@/hooks/usePendingTxs'
import { runExecutionPreChecks } from '@/services/tx/executionPreChecks'
import { getSafeTxGas, getNonces } from '@/services/tx/tx-sender/recommendedNonce'
import useAsync from '@safe-global/utils/hooks/useAsync'
import { useUpdateBatch } from '@/features/batching'
import { useCurrentChain } from '@/hooks/useChains'
import { useLoadFeature } from '@/features/__core__'
import { GTFFeature } from '@/features/gtf'
import { mergeGtfFeeParams } from '@/features/gtf/services'
import { SafeTxContext } from '@/components/tx-flow/SafeTxProvider'
import { useAppDispatch, useAppSelector } from '@/store'
import { selectCurrency } from '@/store/settingsSlice'

type TxActions = {
  addToBatch: (safeTx?: SafeTransaction, origin?: string) => Promise<string>
  signTx: (safeTx?: SafeTransaction, txId?: string, origin?: string) => Promise<string>
  executeTx: (
    txOptions: TransactionOptions,
    safeTx?: SafeTransaction,
    txId?: string,
    origin?: string,
    isRelayed?: boolean,
    acceptUnverifiedSimulation?: boolean,
    /** The Safe Pro Workspace paying for the relay, when the Safe is on a plan. */
    sponsorSpaceId?: string | null,
  ) => Promise<string>
  signProposerTx: (safeTx?: SafeTransaction, origin?: string) => Promise<string>
  proposeTx: (safeTx: SafeTransaction, origin?: string) => Promise<TransactionDetails>
}

/**
 * Returns transaction action functions for signing, executing, and batching
 *
 * @returns Object containing signTx, executeTx, addToBatch, signProposerTx, proposeTx
 */
export const useTxActions = (): TxActions => {
  const { safe } = useSafeInfo()
  const scope = useSafeScope()
  const onboard = useOnboard()
  const signer = useSigner()
  const wallet = useWallet()
  const [addTxToBatch] = useUpdateBatch()
  const chain = useCurrentChain()
  const dispatch = useAppDispatch()
  const gtfFeature = useLoadFeature(GTFFeature)
  const { gtfPaymentMode, gtfSelectedGasToken } = useContext(SafeTxContext)
  const currency = useAppSelector(selectCurrency)

  return useMemo<TxActions>(() => {
    // While a scoped Safe's SafeState is still loading, `safe` is `defaultSafeInfo` — the scope's own
    // chainId/safeAddress are already known, so prefer them over the placeholder.
    const safeAddress = scope?.safeAddress ?? safe.address.value
    const chainId = scope?.chainId ?? safe.chainId

    const withGtfFeeParams = (safeTx: SafeTransaction) =>
      mergeGtfFeeParams({
        safeTx,
        chain,
        gtfPaymentMode,
        gtfSelectedGasToken,
        gtfFeature,
        chainId,
        safeAddress,
        numberSignatures: safe.threshold,
        currency,
        dispatch,
      })

    const _propose = async (sender: string, safeTx: SafeTransaction, origin?: string) => {
      return dispatchTxProposal({ chainId, safeAddress, sender, safeTx, origin, scope })
    }

    // A tx with a txId is already known to CGW, so only the new signature is sent
    const _proposeOrConfirm = async (
      sender: string,
      safeTx: SafeTransaction,
      txId?: string,
      origin?: string,
    ): Promise<string> => {
      if (txId) {
        const confirmedTx = await dispatchTxConfirmation({ chainId, safeAddress, sender, safeTx, txId, scope })
        return confirmedTx.txId
      }
      const proposedTx = await _propose(sender, safeTx, origin)
      return proposedTx.txId
    }

    const proposeTx: TxActions['proposeTx'] = async (safeTx, origin) => {
      assertTx(safeTx)
      return _propose(wallet?.address || safe.owners[0].value, safeTx, origin)
    }

    const addToBatch: TxActions['addToBatch'] = async (safeTx, origin) => {
      assertTx(safeTx)
      assertProvider(signer?.provider)

      const tx = await _propose(signer.address, safeTx, origin)

      await addTxToBatch(tx)
      return tx.txId
    }

    const signRelayedTx = async (safeTx: SafeTransaction, txId?: string): Promise<SafeTransaction> => {
      assertTx(safeTx)
      assertProvider(signer?.provider)

      safeTx = await withGtfFeeParams(safeTx)

      // Smart contracts cannot sign transactions off-chain
      if (await isSmartContractWallet(signer.chainId, signer.address)) {
        throw new Error('Cannot relay an unsigned transaction from a smart contract wallet')
      }
      return await dispatchTxSigning(safeTx, signer.provider, txId, scope)
    }

    const signTx: TxActions['signTx'] = async (safeTx, txId, origin) => {
      assertTx(safeTx)
      assertProvider(signer?.provider)
      assertOnboard(onboard)

      safeTx = await withGtfFeeParams(safeTx)

      // Smart contract wallets must sign via an on-chain tx
      if (signer.isSafe || (await isSmartContractWallet(signer.chainId, signer.address))) {
        const id = txId || (await _propose(signer.address, safeTx, origin)).txId
        await dispatchOnChainSigning(
          safeTx,
          id,
          signer.provider,
          chainId,
          signer.address,
          safeAddress,
          Boolean(signer.isSafe),
          scope,
        )
        return id
      }

      // Otherwise, sign off-chain
      const signedTx = await dispatchTxSigning(safeTx, signer.provider, txId, scope)
      return _proposeOrConfirm(signer.address, signedTx, txId, origin)
    }

    const signProposerTx: TxActions['signProposerTx'] = async (safeTx, origin) => {
      assertTx(safeTx)
      assertProvider(wallet?.provider)
      assertOnboard(onboard)

      const signedTx = await dispatchProposerTxSigning(safeTx, wallet, scope)

      const tx = await _propose(wallet.address, signedTx, origin)
      return tx.txId
    }

    const executeTx: TxActions['executeTx'] = async (
      txOptions,
      safeTx,
      txId,
      origin,
      isRelayed,
      acceptUnverifiedSimulation,
      sponsorSpaceId,
    ) => {
      assertTx(safeTx)
      assertProvider(signer?.provider)
      assertOnboard(onboard)
      assertChainInfo(chain)

      // Catch the three GS026 causes (stale nonce, non-signer executor, bad
      // signature) before anything is signed or broadcast — an on-chain GS026
      // revert would cost the user gas for a guaranteed failure (WA-3005).
      await runExecutionPreChecks({ safeTx, safe, signerAddress: signer.address, scope })

      let rePropose = false
      // Relayed transactions must be fully signed, so request a final signature if needed
      if (isRelayed && safeTx.signatures.size < safe.threshold) {
        safeTx = await signRelayedTx(safeTx)
        rePropose = true
      }

      // Propose the tx if there's no id yet, or send the new signature to the already proposed tx
      if (!txId || rePropose) {
        txId = await _proposeOrConfirm(signer.address, safeTx, txId, origin)
      }

      // Relay or execute the tx via connected wallet
      if (isRelayed) {
        await dispatchTxRelay(
          safeTx,
          safe,
          txId,
          chain,
          txOptions.gasLimit,
          acceptUnverifiedSimulation,
          scope,
          sponsorSpaceId,
        )
      } else {
        const isSmartAccount = await isSmartContractWallet(signer.chainId, signer.address)
        await dispatchTxExecution(
          safe.chainId,
          safeTx,
          txOptions,
          txId,
          signer.provider,
          signer.address,
          safeAddress,
          isSmartAccount,
          scope,
        )
      }

      return txId
    }

    return { addToBatch, signTx, executeTx, signProposerTx, proposeTx }
  }, [
    safe,
    scope,
    wallet,
    signer?.provider,
    signer?.address,
    signer?.chainId,
    signer?.isSafe,
    addTxToBatch,
    onboard,
    chain,
    dispatch,
    gtfFeature,
    gtfPaymentMode,
    gtfSelectedGasToken,
    currency,
  ])
}

export const useValidateNonce = (safeTx: SafeTransaction | undefined): boolean => {
  const { safe } = useSafeInfo()
  return !!safeTx && safeTx?.data.nonce === safe.nonce
}

export const useImmediatelyExecutable = (): boolean => {
  const { safe } = useSafeInfo()
  const hasPending = useHasPendingTxs()
  return safe.threshold === 1 && !hasPending
}

// Check if the executor is the safe itself (it won't work)
export const useIsExecutionLoop = (): boolean => {
  const wallet = useWallet()
  const { safeAddress } = useSafeInfo()
  return wallet ? sameAddress(wallet.address, safeAddress) : false
}

export const useRecommendedNonce = (): number | undefined => {
  const { safeAddress, safe } = useSafeInfo()

  const [recommendedNonce] = useAsync(
    async () => {
      if (!safe.chainId || !safeAddress) return
      if (!safe.deployed) return 0

      const nonces = await getNonces(safe.chainId, safeAddress)

      return nonces?.recommendedNonce
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [safeAddress, safe.chainId, safe.txQueuedTag, safe.txHistoryTag], // update when tx queue or history changes
    false, // keep old recommended nonce while refreshing to avoid skeleton
  )

  return recommendedNonce
}

export const useSafeTxGas = (safeTx: SafeTransaction | undefined): string | undefined => {
  const { safeAddress, safe } = useSafeInfo()

  // Memoize only the necessary params so that the useAsync hook is not called every time safeTx changes
  const safeTxParams = useMemo(() => {
    return !safeTx?.data?.to
      ? undefined
      : {
          to: safeTx?.data.to,
          value: safeTx?.data?.value,
          data: safeTx?.data?.data,
          operation: safeTx?.data?.operation,
        }
  }, [safeTx?.data.to, safeTx?.data.value, safeTx?.data.data, safeTx?.data.operation])

  const [safeTxGas] = useAsync(() => {
    if (!safe.chainId || !safeAddress || !safeTxParams || !safe.version) return

    return getSafeTxGas(safe.chainId, safeAddress, safe.version, safeTxParams)
  }, [safeAddress, safe.chainId, safe.version, safeTxParams])

  return safeTxGas
}

export const useAlreadySigned = (safeTx: SafeTransaction | undefined): boolean => {
  const wallet = useSigner()
  const hasSigned =
    safeTx && wallet && (safeTx.signatures.has(wallet.address.toLowerCase()) || safeTx.signatures.has(wallet.address))
  return Boolean(hasSigned)
}
